/// <amd-module name="VSSUI/Components/IdentityPickerDropdown/CustomIdentityPickerDropdown" />

import * as React from "react";
import * as Resources from "VSSUI/Resources.IdentityPicker";

import { ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ICancelablePromise, makeCancelable } from "VSS/Core/Util/Promise";
import { format, startsWith } from "VSS/Core/Util/String";
import { FocusWithin, IFocusWithinStatus } from "VSSUI/FocusWithin";
import { Icon, IconSize } from "VSSUI/Icon";
import { Measure } from "VSSUI/Measure";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { TextField } from "VSSUI/TextField";
import { css, KeyCode } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { IdentityPickerSuggestionItem } from "../IdentityPickerSuggestionsList/IdentityPickerSuggestionItem";
import { IdentityPickerSuggestionsList } from "../IdentityPickerSuggestionsList/IdentityPickerSuggestionsList";
import { IIdentitySuggestionItemProps } from "../IdentityPickerSuggestionsList/IdentityPickerSuggestionsList.Props";
import { ICustomIdentityPickerDropdownProps } from "./CustomIdentityPickerDropdown.Props";
import { shouldShowIdentityCard } from "./IdentityPickerUtils";
import { IdentityType, IIdentity } from "./SharedIdentityPicker.Props";

interface ICustomIdentityPickerDropdownState {
    width: number;
}

let textFieldId = 1;

export class CustomIdentityPickerDropdown extends React.Component<ICustomIdentityPickerDropdownProps, ICustomIdentityPickerDropdownState> {
    private cachedResults: { [id: string]: IIdentity[] };
    private currentPromise: ICancelablePromise<IIdentity[]>;
    private focusWithin = React.createRef<FocusWithin>();
    private inputElement = React.createRef<TextField>();
    private itemRefs: { [id: string]: IdentityPickerSuggestionItem } = {};
    private updateIndexTimer: number | undefined;
    private openedIdentityCard = new ObservableValue<IIdentity | undefined>(undefined);
    private outerElement = React.createRef<HTMLDivElement>();
    private lastPickedIdentity: IIdentity;
    private nonIdentitySuggestions: IIdentity[] = [];

    private suggestionsLoading = new ObservableValue<boolean>(false);
    private isEditing = new ObservableValue<boolean>(false);
    private selectedIndex = new ObservableValue<number>(-1);

    private suggestions: ObservableArray<IIdentity> = new ObservableArray<IIdentity>([]);
    private timerManagement: TimerManagement;

    private textFieldId = `identity-picker-downdown-textfield-${textFieldId++}`;

    constructor(props: ICustomIdentityPickerDropdownProps) {
        super(props);

        this.cachedResults = {};
        this.timerManagement = new TimerManagement();
        this.lastPickedIdentity = ObservableLike.getValue(props.value);
        this.state = { width: 296 };
    }

    public render(): JSX.Element {
        const {
            ariaLabel,
            ariaLabelledBy,
            autoFocus,
            disabled,
            editPlaceholder = Resources.IdentityPickerPlaceholderFocusText,
            placeholder = Resources.IdentityPickerPlaceholderText,
            required
        } = this.props;
        return (
            <Observer suggestionsVisible={this.props.suggestionsVisible}>
                {(topProps: { suggestionsVisible: boolean }) => {
                    return (
                        <FocusWithin onBlur={this.onBlur} onFocus={this.onFocus} ref={this.focusWithin}>
                            {(focusStatus: IFocusWithinStatus) => {
                                return (
                                    <>
                                        <Observer
                                            selectedIdentity={this.props.value}
                                            selectedIndex={this.selectedIndex}
                                            suggestionsLoading={this.suggestionsLoading}
                                            textValue={this.props.textValue}
                                        >
                                            {(props: {
                                                selectedIdentity: IIdentity;
                                                selectedIndex: number;
                                                suggestionsLoading: boolean;
                                                textValue: string;
                                            }) => {
                                                let ariaActiveDescendantId;

                                                if (topProps.suggestionsVisible) {
                                                    if (props.selectedIndex === -1 || props.suggestionsLoading) {
                                                        ariaActiveDescendantId = "sug-list-transition";
                                                    } else if (!this.suggestions || !this.suggestions.length) {
                                                        ariaActiveDescendantId = "sug-list-no-results";
                                                    } else {
                                                        ariaActiveDescendantId = `sug-row-${props.selectedIndex}`;
                                                    }
                                                }

                                                const ariaControlsId = topProps.suggestionsVisible ? "tag-picker-callout" : undefined;
                                                const placeholderText =
                                                    !focusStatus.hasFocus && !props.selectedIdentity ? placeholder : editPlaceholder;
                                                return (
                                                    <Measure onMeasure={this.onTextFieldChanged}>
                                                        <div
                                                            className="bolt-identitypickerdropdown flex-row flex-grow"
                                                            ref={this.outerElement}
                                                            onKeyDown={this.onKeyDown}
                                                        >
                                                            <TextField
                                                                ariaExpanded={topProps.suggestionsVisible}
                                                                ariaActiveDescendant={ariaActiveDescendantId}
                                                                ariaAutoComplete="list"
                                                                ariaControls={ariaControlsId}
                                                                autoFocus={autoFocus}
                                                                ariaHasPopup="listbox"
                                                                ariaLabel={
                                                                    ariaLabel ? ariaLabel : props.textValue === "" ? placeholderText : props.textValue
                                                                }
                                                                ariaLabelledBy={ariaLabelledBy}
                                                                className={css(
                                                                    this.props.className,
                                                                    "bolt-identitypickerdropdown-textField flex-row flex-center",
                                                                    focusStatus.hasFocus && "bolt-identitypickerdropdown-open"
                                                                )}
                                                                containerClassName="bolt-identitypickerdropdown-container flex-column flex-grow"
                                                                inputId={this.props.inputId ?? this.textFieldId}
                                                                onBlur={focusStatus.onBlur}
                                                                onChange={this.onSearchChange}
                                                                onClick={this.onClick}
                                                                onFocus={focusStatus.onFocus}
                                                                prefixIconProps={{ render: this.renderPersonaCoin }}
                                                                placeholder={placeholderText}
                                                                ref={this.inputElement}
                                                                required={required}
                                                                role="combobox"
                                                                suffixIconProps={
                                                                    props.textValue && !disabled
                                                                        ? {
                                                                              ariaHidden: "false",
                                                                              iconName: "Clear",
                                                                              className: "bolt-identity-picker-clearButton fontSize",
                                                                              role: "button",
                                                                              ariaLabel: format(Resources.Remove, props.textValue),
                                                                              onClick: this.onClearClicked,
                                                                              onKeyDown: this.onClearKeyDown,
                                                                              tabIndex: 0
                                                                          }
                                                                        : undefined
                                                                }
                                                                value={props.textValue}
                                                                disabled={disabled}
                                                            />
                                                        </div>
                                                    </Measure>
                                                );
                                            }}
                                        </Observer>
                                        <Observer openedIdentityCard={this.openedIdentityCard} selectedIndex={this.selectedIndex}>
                                            {(props: { openedIdentityCard: IIdentity; selectedIndex: number }) => {
                                                return (
                                                    <IdentityPickerSuggestionsList
                                                        calloutProps={{
                                                            anchorElement: this.outerElement.current,
                                                            anchorOrigin: { horizontal: Location.start, vertical: Location.end },
                                                            calloutOrigin: { horizontal: Location.start, vertical: Location.start },
                                                            contentShadow: true,
                                                            id: "tag-picker-callout",
                                                            onDismiss: this.onPickerDismiss,
                                                            role: "presentation"
                                                        }}
                                                        suggestionsVisible={topProps.suggestionsVisible || !!props.openedIdentityCard}
                                                        isLoading={this.suggestionsLoading}
                                                        onBlur={focusStatus.onBlur}
                                                        onFocus={focusStatus.onFocus}
                                                        onSuggestionClicked={this.onSuggestionClick}
                                                        onClosePersonaCard={this.closePersonaCard}
                                                        onDismiss={this.onPickerDismiss}
                                                        onOpenPersonaCard={this.openPersonaCard}
                                                        openedIdentityCard={props.openedIdentityCard}
                                                        pickerProvider={this.props.pickerProvider}
                                                        renderSuggestion={this.renderSuggestionItem}
                                                        suggestions={this.suggestions}
                                                        suggestionTarget={this.outerElement.current}
                                                        selectedIndex={props.selectedIndex}
                                                        width={this.state.width}
                                                    />
                                                );
                                            }}
                                        </Observer>
                                    </>
                                );
                            }}
                        </FocusWithin>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount(): void {
        this.updateValue = this.timerManagement.debounce(this.updateValue, 250);
        if (this.props.autoFocus) {
            const textValue = ObservableLike.getValue(this.props.textValue);
            if (this.props.pickerProvider.onEmptyInputFocus) {
                this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
            } else {
                this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
            }

            this.inputElement.current && this.inputElement.current.select();
            this.props.onSuggestionsVisibleChanged(true);
        }
        this.setState({ width: this.outerElement.current.clientWidth });
    }

    public componentWillUnmount(): void {
        this.currentPromise && this.currentPromise.cancel();
    }

    public updateSuggestionsList(suggestions: IIdentity[] | PromiseLike<IIdentity[]>, initialSearchValue?: string): void {
        const suggestionsArray: IIdentity[] = suggestions as IIdentity[];
        const suggestionsPromiseLike: Promise<IIdentity[]> = suggestions as Promise<IIdentity[]>;

        let filteredNonIdentitySuggestions: IIdentity[] = [];
        if (this.props.pickerProvider.getAdditionalEntries) {
            this.nonIdentitySuggestions = this.props.pickerProvider.getAdditionalEntries().map(value => {
                return { displayName: value, entityType: IdentityType.Custom, entityId: value, originDirectory: "", originId: "" };
            });

            filteredNonIdentitySuggestions =
                this.nonIdentitySuggestions && initialSearchValue
                    ? this.nonIdentitySuggestions.filter(s => startsWith(s.displayName, initialSearchValue))
                    : this.nonIdentitySuggestions;
        }

        // Check to see if the returned value is an array, if it is then just pass it into the next function.
        // If the returned value is not an array then check to see if it's a promise or PromiseLike. If it is then resolve it asynchronously.
        if (Array.isArray(suggestionsArray)) {
            const resultSuggestions = filteredNonIdentitySuggestions.length
                ? [...suggestionsArray, ...filteredNonIdentitySuggestions]
                : suggestionsArray;
            this.updateSuggestions(resultSuggestions, initialSearchValue);
        } else if (suggestionsPromiseLike && suggestionsPromiseLike.then) {
            this.suggestionsLoading.value = true;

            // Ensure that the promise will only use the callback if it was the most recent one.
            const promise: ICancelablePromise<IIdentity[]> = (this.currentPromise = makeCancelable(suggestionsPromiseLike));
            promise.promise.then((newSuggestions: IIdentity[]) => {
                if (promise === this.currentPromise) {
                    const resultSuggestions = filteredNonIdentitySuggestions.length
                        ? [...newSuggestions, ...filteredNonIdentitySuggestions]
                        : newSuggestions;
                    this.updateSuggestions(resultSuggestions, initialSearchValue);

                    if (!!initialSearchValue && initialSearchValue !== "" && this.suggestions.value && this.suggestions.value.length > 0) {
                        this.cachedResults[initialSearchValue] = resultSuggestions;
                    }

                    this.suggestionsLoading.value = false;
                }
            });
        }
    }

    private onPickerDismiss = (): void => {
        this.props.onSuggestionsVisibleChanged(false);
    };

    private renderSuggestionItem = (suggestion: IIdentitySuggestionItemProps): JSX.Element => {
        return (
            <div className="flex-row flex-grow scroll-hidden" onKeyDown={this.onKeyDownSuggestionItem}>
                <IdentityPickerSuggestionItem
                    {...suggestion}
                    onOpenPersonaCard={this.openPersonaCard}
                    ref={itemRef => (this.itemRefs[suggestion.item.entityId] = itemRef)}
                    renderSuggestion={this.props.renderCustomIdentitySuggestion && this.renderCustomSuggestionItem}
                />
            </div>
        );
    };

    private renderCustomSuggestionItem = (suggestion: IIdentitySuggestionItemProps): JSX.Element => {
        return this.props.renderCustomIdentitySuggestion && this.props.renderCustomIdentitySuggestion(suggestion.item);
    };

    private renderPersonaCoin = (className: string): JSX.Element => {
        return (
            <Observer selectedIdentity={this.props.value} isEditing={this.isEditing}>
                {(props: { selectedIdentity: IIdentity; isEditing: boolean }) => {
                    return props.selectedIdentity && !props.isEditing ? (
                        <Persona
                            className={css("flex-row flex-center justify-center", className)}
                            identity={props.selectedIdentity}
                            size={PersonaSize.size24}
                        />
                    ) : (
                        <Icon className={css("flex-row flex-center justify-center", className)} iconName="Contact" size={IconSize.medium} />
                    );
                }}
            </Observer>
        );
    };

    private openPersonaCard = (identity: IIdentity): void => {
        if (shouldShowIdentityCard(identity)) {
            this.openedIdentityCard.value = identity;
        }
    };

    private closePersonaCard = (): void => {
        this.openedIdentityCard.value = undefined;

        if (this.inputElement.current) {
            this.inputElement.current.focus();
            const suggestionsVisible = ObservableLike.getValue(this.props.suggestionsVisible);
            if (!suggestionsVisible) {
                this.props.onSuggestionsVisibleChanged(true);
            }
        }
    };

    private completeSuggestion = (): boolean => {
        let selectedIdentity: IIdentity | undefined;

        if (this.suggestions.value.length > 0 && ObservableLike.getValue(this.props.suggestionsVisible) && this.selectedIndex.value !== -1) {
            selectedIdentity = this.suggestions.value[this.selectedIndex.value];
        } else {
            this.selectedIndex.value = -1;
            selectedIdentity =
                this.props.resolveUnrecognizedIdentity && this.props.resolveUnrecognizedIdentity(ObservableLike.getValue(this.props.textValue));
        }

        this.selectedPersonaChanged(selectedIdentity);
        if (selectedIdentity) {
            this.props.onSuggestionsVisibleChanged(false);
        }

        return Boolean(selectedIdentity);
    };

    private onBlur = (): void => {
        this.props.onSuggestionsVisibleChanged(false);
        if (!this.openedIdentityCard.value) {
            ObservableLike.getValue(this.props.textValue) === "" && this.selectedPersonaChanged();
            this.props.onBlur && this.props.onBlur();
        }
    };

    private onClearClicked = (ev: React.MouseEvent<HTMLSpanElement>): void => {
        this.clear();
        ev.preventDefault();
    };

    private onClearKeyDown = (ev: React.KeyboardEvent<HTMLSpanElement>): void => {
        if (ev.which === KeyCode.enter) {
            this.clear();
            ev.preventDefault();
        }
    };

    private clear = (): void => {
        this.props.onClear && this.props.onClear();
        this.props.onChange(undefined);
        this.lastPickedIdentity = undefined;
        this.suggestions.value = [];
        this.inputElement.current?.focus();
    };

    private onClick = (): void => {
        if (
            (ObservableLike.getValue(this.props.textValue) === "" && this.props.pickerProvider.onEmptyInputFocus) ||
            (ObservableLike.getValue(this.props.value) && this.suggestions.length === 0)
        ) {
            this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
            this.props.onSuggestionsVisibleChanged(!ObservableLike.getValue(this.props.suggestionsVisible));
        } else {
            this.props.onSuggestionsVisibleChanged(!ObservableLike.getValue(this.props.suggestionsVisible));
        }
        this.inputElement.current.select();
    };

    private onFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
        const { onFocus } = this.props;
        if (
            ObservableLike.getValue(this.props.textValue) === "" &&
            this.props.pickerProvider.onEmptyInputFocus &&
            !ObservableLike.getValue(this.props.suggestionsVisible)
        ) {
            this.updateSuggestionsList(this.props.pickerProvider.onEmptyInputFocus());
        }

        if (onFocus) {
            onFocus(e);
        }
    };

    private onKeyDown = (ev: React.KeyboardEvent<HTMLElement>): void => {
        if (ev.isDefaultPrevented()) {
            return;
        }
        const keyCode = ev.which;
        const suggestionsVisible = ObservableLike.getValue(this.props.suggestionsVisible);
        const input = this.inputElement.current && this.inputElement.current.inputElement.current;
        switch (keyCode) {
            case KeyCode.escape:
                if (this.openedIdentityCard.value) {
                    !(this.suggestions.value && this.suggestions.value[this.selectedIndex.value]) && this.selectedPersonaChanged();
                }
                if (suggestionsVisible) {
                    this.props.onSuggestionsVisibleChanged(false);
                    this.openedIdentityCard.value = undefined;
                    ev.stopPropagation();
                }

                break;

            case KeyCode.tab:
            case KeyCode.enter:
                if (!ev.shiftKey && suggestionsVisible) {
                    if (this.completeSuggestion()) {
                        ev.preventDefault();
                        ev.stopPropagation();
                    }
                } else if (suggestionsVisible) {
                    this.completeSuggestion();
                } else if (keyCode === KeyCode.enter && input && !input.value) {
                    // Enter on an empty input element should behave the same as the clear button
                    this.selectedPersonaChanged(undefined);
                }
                break;

            case KeyCode.rightArrow:
                if (
                    this.suggestions.value &&
                    this.suggestions.value[this.selectedIndex.value] &&
                    input &&
                    input.value.length === input.selectionEnd
                ) {
                    this.focusContactCardButton(this.suggestions.value[this.selectedIndex.value]);
                    ev.preventDefault();
                }
                break;

            case KeyCode.upArrow:
                if (suggestionsVisible && this.suggestions.value) {
                    this.selectedIndex.value = Math.max(0, this.selectedIndex.value - 1);
                    this.forceUpdate();
                    ev.preventDefault();
                    ev.stopPropagation();
                }

                break;

            case KeyCode.downArrow:
                if (suggestionsVisible && this.suggestions.value) {
                    this.selectedIndex.value = Math.min(this.suggestions.value.length - 1, this.selectedIndex.value + 1);
                    this.forceUpdate();
                    ev.preventDefault();
                    ev.stopPropagation();
                } else {
                    this.props.onSuggestionsVisibleChanged(true);
                }

                break;
        }
    };

    private onKeyDownSuggestionItem = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            if (event.which === KeyCode.leftArrow || event.which === KeyCode.escape || event.which === KeyCode.tab) {
                if (this.inputElement.current) {
                    this.inputElement.current.focus();
                    event.preventDefault();
                }
            }
        }
    };

    private focusContactCardButton = (tag: IIdentity) => {
        if (this.itemRefs[tag.entityId]) {
            this.itemRefs[tag.entityId].focus();
        }
    };

    private onResolveSuggestions = (updatedValue: string): void => {
        const suggestions: PromiseLike<IIdentity[]> | IIdentity[] = this.props.pickerProvider.onFilterIdentities(
            updatedValue.toLocaleLowerCase(),
            []
        );

        if (suggestions !== null) {
            this.updateSuggestionsList(suggestions, updatedValue);
        }
    };

    private onSearchChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        if (value.length == 0) {
            const { onClear } = this.props;
            onClear && onClear();
        }
        this.selectedIndex.value = -1;
        this.isEditing.value = true;
        this.props.onInputChange(value);
        this.updateValue(value);
    };

    private onSuggestionClick = (suggestion: IIdentitySuggestionItemProps): void => {
        this.selectedIndex.value = suggestion.index;
        this.selectedPersonaChanged(suggestion.item);
        this.props.onSuggestionsVisibleChanged(false);
    };

    private onTextFieldChanged = (newWidth: number, newHeight: number): void => {
        this.setState({ width: Math.max(newWidth, 296) });
    };

    private selectedPersonaChanged = (persona?: IIdentity): void => {
        if (this.lastPickedIdentity !== persona) {
            !!persona && !!this.props.pickerProvider.addIdentitiesToMRU && this.props.pickerProvider.addIdentitiesToMRU([persona]);
        }
        if (this.props.onChange(persona) !== false) {
            this.props.onInputChange(persona ? persona.displayName : "");
            this.lastPickedIdentity = persona;
        } else {
            this.props.onInputChange("");
            this.props.onChange(undefined);
            this.lastPickedIdentity = undefined;
        }

        this.isEditing.value = false;
    };

    private setSuggestions(suggestions: IIdentity[], selectedIndex: number): void {
        this.suggestions.value = suggestions;

        // Selected index set after list is updated for screen readers.
        if (this.updateIndexTimer) {
            window.cancelAnimationFrame(this.updateIndexTimer);
        }
        this.updateIndexTimer = window.requestAnimationFrame(() => {
            this.selectedIndex.value = selectedIndex;
        });
    }

    private updateSuggestions(suggestions: IIdentity[], initialSearchValue?: string): void {
        // Only update the suggestions if the initial search value is the same as the current input
        if (initialSearchValue === undefined || (this.inputElement.current && ObservableLike.getValue(this.props.textValue) === initialSearchValue)) {
            this.setSuggestions(suggestions, ObservableLike.getValue(this.props.textValue) === "" ? -1 : 0);
        }
    }

    private updateValue = (updatedValue: string): void => {
        this.props.onSuggestionsVisibleChanged(!!updatedValue);
        if (this.cachedResults[updatedValue]) {
            this.updateSuggestionsList(this.cachedResults[updatedValue], updatedValue);
        } else {
            this.onResolveSuggestions(updatedValue);
        }
    };
}
